'use client';

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  TopInsiderCompany,
  InsiderTradesManager,
  InsidersLandingRequestOptions,
} from '@benzinga/insider-trades-manager';
import { SessionContext } from '@benzinga/session-context';
import type { ColumnDef } from '@benzinga/table';
import { Table } from '@benzinga/table';

import { PermissionOverlay } from '../../../edge/dashboard/components/PermissionOverlay';
import {
  getNetBarScaleMaxFromDataset,
  NetBuySellBarCellRenderer,
  NetVolumeGraphLegend,
  getNetOnlyScaleMaxFromDataset,
} from './NetBuySellBarCellRenderer';
import {
  INSIDER_TIME_PERIOD_OPTIONS,
  TRADE_TYPE_OPTIONS,
  INSIDER_ROLE_OPTIONS,
  type InsiderRoleFilterValue,
} from './filterOptions';
import { InsiderFilterSelect } from './InsiderFilterSelect';
import { useInsiderEdgeGate } from './useInsiderEdgeGate';

import { getNetVolumeColumnDefs } from './columnDefs/netVolumeColumnsDef';
import { NetVolumeCell, TickerCell } from './columnDefs';
import { formatSharesCompact, formatShareAxisLabel } from './formatters';

import styles from '../../insiderTrades.module.scss';
import '../../table.scss';
import './agGridRegister';

const PERIOD_SELECT_OPTIONS = INSIDER_TIME_PERIOD_OPTIONS.map(o => ({
  value: String(o.value),
  label: o.label,
}));

interface TopInsiderCompaniesTableProps {
  data: { companies?: unknown[]; data?: unknown[] } | null;
}

function initialRowsFromProps(data: TopInsiderCompaniesTableProps['data']): TopInsiderCompany[] {
  const list = data?.data ?? data?.companies;
  if (!Array.isArray(list)) return [];
  return list as TopInsiderCompany[];
}

/** Matches `getTopInsiderCompanies()` defaults used on the insider-trades landing page (SSR). */
function filtersMatchSsrTopCompanies(period: number, transactionType: string, role: InsiderRoleFilterValue): boolean {
  return period === INSIDER_TIME_PERIOD_OPTIONS[0].value && transactionType === 'all' && role === 'all';
}

export function TopInsiderCompaniesTable({ data }: TopInsiderCompaniesTableProps) {
  const [view, setView] = useState<'graph' | 'table'>('table');
  const [period, setPeriod] = useState(30);
  const [transactionType, setTransactionType] = useState('all');
  const [role, setRole] = useState<InsiderRoleFilterValue>('all');
  const [rows, setRows] = useState<TopInsiderCompany[]>(() => initialRowsFromProps(data));
  const [loading, setLoading] = useState(false);
  const skipInitialFetchIfSsr = useRef(true);
  const session = useContext(SessionContext);
  const insiderTradesManager = session.getManager(InsiderTradesManager);
  const { canLoadGatedData, isPaywalled } = useInsiderEdgeGate();

  const fetchTopCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const options: InsidersLandingRequestOptions = {
        period,
        trade_types: transactionType !== 'all' ? transactionType.split(',').map(t => t.trim()) : undefined,
        ...(role !== 'all' ? { insider_role: role } : {}),
      };
      const result = await insiderTradesManager.getTopInsiderCompanies(options);
      if (result.ok) {
        setRows(result.ok.data ?? []);
      } else {
        setRows([]);
      }
    } catch (_e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [period, transactionType, role, insiderTradesManager]);

  useEffect(() => {
    if (!canLoadGatedData) {
      setLoading(false);
      return;
    }
    if (skipInitialFetchIfSsr.current) {
      skipInitialFetchIfSsr.current = false;
      if (!filtersMatchSsrTopCompanies(period, transactionType, role)) {
        return;
      }
    }
    void fetchTopCompanies();
  }, [canLoadGatedData, fetchTopCompanies, period, transactionType, role]);

  const graphScaleMax = useMemo(() => getNetBarScaleMaxFromDataset(rows), [rows]);

  const graphColumnDefs = useMemo<ColumnDef<TopInsiderCompany>[]>(
    () => [
      {
        field: 'company_ticker',
        headerName: 'Ticker',
        width: 100,
        cellRenderer: (params: { data: TopInsiderCompany }) => {
          const row = params.data;
          if (!row) return null;
          return (
            <TickerCell cik={row.company_cik ?? ''} ticker={row.company_ticker ?? ''} name={row.company_name ?? ''} />
          );
        },
      },
      {
        headerName: 'Net Buying vs Selling',
        colId: 'netBar',
        field: 'net_volume',
        flex: 1,
        minWidth: 380,
        cellRenderer: (params: { data: TopInsiderCompany; index: number; value: any }) => (
          <NetBuySellBarCellRenderer {...params} scaleMax={graphScaleMax} />
        ),
      },
      {
        field: 'net_volume',
        headerName: 'Volume (Net)',
        width: 110,
        cellRenderer: ({ value }: { value: number | undefined }) => {
          if (value == null) return '-';
          const val = Number(value);
          const isPositive = value >= 0;
          return <NetVolumeCell value={val} isPositive={isPositive} />;
        },
      },
    ],
    [graphScaleMax],
  );

  const tableColumnDefs = useMemo<ColumnDef<TopInsiderCompany>[]>(() => getNetVolumeColumnDefs(), []);
  const columnDefs = view === 'graph' ? graphColumnDefs : tableColumnDefs;

  const tableBody = (
    <div
      className={`${styles['insider-table-section-body']} ${styles['insider-trades-table-wrapper']} insider-trades-table-wrapper`}
    >
      <Table<TopInsiderCompany>
        rowData={rows}
        columnsDef={columnDefs as React.ComponentProps<typeof Table<TopInsiderCompany>>['columnsDef']}
        isLoading={loading}
        isGated={isPaywalled}
        gatedRowBlur="row"
        gatedVisibleRowCount={0}
      />
      {view === 'graph' && (
        <table className={`table-auto w-full px-4 mt-2`}>
          <tfoot>
            <NetVolumeGraphLegend scaleMax={graphScaleMax} />
          </tfoot>
        </table>
      )}
    </div>
  );

  return (
    <div className={styles['insider-table-section']}>
      <div className={styles['insider-table-section-header']}>
        <h3 className={styles['section-title']}>
          Top Insider Trades By Net $ Volume Buying Vs. Selling (Last {period} Days)
        </h3>
        <div className={styles['insider-table-filters']}>
          <InsiderFilterSelect
            id="top-time-period"
            label="Time Period"
            ariaLabel="Time period"
            value={String(period)}
            disabled={isPaywalled}
            onValueChange={v => setPeriod(Number(v))}
            options={PERIOD_SELECT_OPTIONS}
          />
          <InsiderFilterSelect
            id="top-transaction"
            label="Transaction"
            ariaLabel="Transaction type"
            value={transactionType}
            disabled={isPaywalled}
            onValueChange={setTransactionType}
            options={TRADE_TYPE_OPTIONS.map(o => ({ value: o.value, label: o.label }))}
          />
          <InsiderFilterSelect
            id="top-role"
            label="Insider Role"
            ariaLabel="Insider role"
            value={role}
            disabled={isPaywalled}
            onValueChange={v => setRole(v as InsiderRoleFilterValue)}
            options={INSIDER_ROLE_OPTIONS}
          />
          <div className={styles['view-toggle']}>
            <button
              type="button"
              className={view === 'graph' ? styles['view-btn-active'] : styles['view-btn']}
              disabled={isPaywalled}
              onClick={() => setView('graph')}
              aria-pressed={view === 'graph'}
            >
              <GraphIcon />
              Graph View
            </button>
            <button
              type="button"
              className={view === 'table' ? styles['view-btn-active'] : styles['view-btn']}
              disabled={isPaywalled}
              onClick={() => setView('table')}
              aria-pressed={view === 'table'}
            >
              <TableIcon />
              Table View
            </button>
          </div>
        </div>
      </div>

      <PermissionOverlay>{tableBody}</PermissionOverlay>
    </div>
  );
}

function GraphIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M3 17v-4h4v4H3zm0-6V7h4v4H3zm6 6v-4h4v4H9zm0-6V7h4v4H9zm6 6v-4h4v4h-4zm0-6V7h4v4h-4z" />
    </svg>
  );
}

function TableIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <rect x="3" y="3" width="18" height="18" rx="1" />
      <path d="M3 9h18M3 15h18M9 3v18M15 3v18" />
    </svg>
  );
}

const AXIS_FRACS = [1, 0.5, 0.25] as const;

const HEADER_CHART_TOP_N = 5;

export const HeaderNetVolumeChart = ({ data }: { data: { data?: unknown[] } | null }) => {
  const rowData: TopInsiderCompany[] = useMemo(() => {
    const list = data?.data ?? [];
    if (!Array.isArray(list)) return [];
    return (list as TopInsiderCompany[])
      .slice(0, HEADER_CHART_TOP_N)
      .sort((a, b) => (b.net_volume ?? 0) - (a.net_volume ?? 0))
      .map(item => item as TopInsiderCompany);
  }, [data]);

  const scaleMax = useMemo(() => getNetOnlyScaleMaxFromDataset(rowData), [rowData]);
  const buyTotal = useMemo(
    () => rowData.reduce((acc, row) => acc + Math.max(0, Number(row.net_volume ?? 0)), 0),
    [rowData],
  );
  const sellTotal = useMemo(
    () => rowData.reduce((acc, row) => acc + Math.min(0, Number(row.net_volume ?? 0)), 0),
    [rowData],
  );

  return (
    <div className={styles['header-chart-card']}>
      <div className={styles['header-chart-header']}>
        <div className={styles['header-chart-header-text']}>
          <h3 className={styles['header-chart-title']}>Top Net Buying vs Selling</h3>
          <p className={styles['header-chart-subtitle']}>{HEADER_CHART_TOP_N} most active tickers · last 30 days</p>
        </div>
        <div className={styles['header-chart-totals']}>
          <div className={styles['header-chart-total']}>
            <span className={styles['header-chart-total-label']}>Net buy total</span>
            {buyTotal > 0 ? (
              <span className={styles['header-chart-total-pos']}>+${formatSharesCompact(buyTotal)}</span>
            ) : (
              <span className={styles['header-chart-total-muted']}>+$0</span>
            )}
          </div>
          <div className={styles['header-chart-total-divider']} />
          <div className={styles['header-chart-total']}>
            <span className={styles['header-chart-total-label']}>Net sell total</span>
            {sellTotal < 0 ? (
              <span className={styles['header-chart-total-neg']}>-${formatSharesCompact(Math.abs(sellTotal))}</span>
            ) : (
              <span className={styles['header-chart-total-muted']}>$0</span>
            )}
          </div>
        </div>
      </div>
      {rowData.length > 0 ? (
        <>
          <div className={styles['header-chart-body']}>
            <div className={styles['header-chart-rows']}>
              {rowData.map((row, idx) => {
                const { buy_volume, net_volume, sell_volume } = row;
                const netVal = net_volume ? Number(net_volume) : 0;
                const isPositive = netVal >= 0;
                const netPct = Math.min(50, (Math.abs(netVal) / scaleMax) * 50);
                const valueStr = isPositive
                  ? `+$${formatSharesCompact(netVal)}`
                  : `-$${formatSharesCompact(Math.abs(netVal))}`;
                const tooltip =
                  buy_volume != null && sell_volume != null
                    ? `Buy $${formatSharesCompact(Number(buy_volume))} • Sell $${formatSharesCompact(Number(sell_volume))}`
                    : undefined;

                return (
                  <div key={idx} className={styles['header-chart-row']}>
                    <div className={styles['header-chart-ticker']}>
                      <TickerCell
                        cik={row.company_cik ?? ''}
                        ticker={row.company_ticker ?? ''}
                        name={row.company_name ?? ''}
                      />
                    </div>
                    <div className={styles['header-chart-bar-area']}>
                      <div className={styles['header-chart-bar-track']} title={tooltip}>
                        {AXIS_FRACS.map(frac => (
                          <span
                            key={`tick-sell-${frac}`}
                            className={styles['header-chart-bar-tick']}
                            style={{ left: `${50 - frac * 50}%` }}
                          />
                        ))}
                        {AXIS_FRACS.map(frac => (
                          <span
                            key={`tick-buy-${frac}`}
                            className={styles['header-chart-bar-tick']}
                            style={{ left: `${50 + frac * 50}%` }}
                          />
                        ))}
                        <div className={styles['header-chart-bar-axis']} />
                        <div
                          className={isPositive ? styles['header-chart-bar-buy'] : styles['header-chart-bar-sell']}
                          style={
                            isPositive
                              ? { width: `${netPct}%`, left: '50%' }
                              : { width: `${netPct}%`, left: `${50 - netPct}%` }
                          }
                        />
                      </div>
                    </div>
                    <div className={isPositive ? styles['header-chart-value-pos'] : styles['header-chart-value-neg']}>
                      {valueStr}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className={styles['header-chart-axis-row']}>
              <div className={styles['header-chart-axis-spacer']} />
              <div className={styles['header-chart-axis']}>
                {[...AXIS_FRACS].map((f, i) => (
                  <span key={`sa${i}`} className={styles['header-chart-axis-tick']} style={{ left: `${50 - f * 50}%` }}>
                    -${formatShareAxisLabel(f * scaleMax)}
                  </span>
                ))}
                <span className={styles['header-chart-axis-zero']} style={{ left: '50%' }}>
                  0
                </span>
                {[...AXIS_FRACS].reverse().map((f, i) => (
                  <span key={`ba${i}`} className={styles['header-chart-axis-tick']} style={{ left: `${50 + f * 50}%` }}>
                    +${formatShareAxisLabel(f * scaleMax)}
                  </span>
                ))}
              </div>
              <div className={styles['header-chart-value-spacer']} />
            </div>
          </div>
        </>
      ) : (
        <div className={styles['header-chart-empty']}>No insider activity available for this window.</div>
      )}
    </div>
  );
};
